var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { cloneDeep, get, has, isArray, isEqual, isPlainObject, map, mapValues, set } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteByPrefix, formatInteger, formatMoney, formatNumber, parseInteger, parseMoney, setNested, swapByPrefixes } from '../../common/helpers';
import useFetch from '../../common/useFetch';
import config from '../../config';
import gs from '../../styles/style';
;
;
;
;
var useForm = function (options) {
    var defaultState = {
        values: __assign({}, options.values),
        initialValues: cloneDeep(options.values) || {},
        formattedValues: setNested(cloneDeep(options.values) || {}, null),
        valid: {},
        errors: {}
    };
    var _a = useState(__assign({}, defaultState)), state = _a[0], setState = _a[1];
    var getTypeHandler = function (type) {
        var baseHandler = {
            parse: function (value) {
                return value;
            },
            format: function (value) {
                return value + '';
            },
            validate: function (value, rules) {
                return true;
            }
        };
        var handlers = {
            string: __assign({}, baseHandler),
            integer: {
                parse: function (value) {
                    return parseInteger(value);
                },
                format: function (value) {
                    return formatInteger(value);
                },
                validate: function (value, rules) {
                    if (rules === void 0) { rules = { min: null, max: 2e20 }; }
                    var intValue = parseInteger(value);
                    if ((intValue + '') != value) {
                        return false;
                    }
                    if (rules.min !== null && intValue < rules.min) {
                        return false;
                    }
                    if (rules.max !== null && intValue > rules.max) {
                        return false;
                    }
                    return true;
                }
            },
            money: {
                parse: function (value) {
                    return parseMoney(value);
                },
                format: function (value) {
                    return formatMoney(value, { symbol: null });
                },
                validate: function (value, rules) {
                    if (rules === void 0) { rules = { min: null, max: 2e20 }; }
                    var moneyValue = parseMoney(value);
                    if (isNaN(moneyValue)) {
                        return false;
                    }
                    if (rules.min !== null && moneyValue < rules.min) {
                        return false;
                    }
                    if (rules.max !== null && moneyValue > rules.max) {
                        return false;
                    }
                    return true;
                }
            },
            date: {
                parse: function (value) {
                    return moment(value, config.input_date_format).format(config.internal_date_format);
                },
                format: function (value) {
                    return moment(value, config.internal_date_format).format(config.input_date_format);
                },
                validate: function (value) {
                    return moment(value, config.input_date_format).isValid();
                }
            },
            dateTime: {
                parse: function (value) {
                    return moment(value, config.input_date_time_format).format(config.internal_date_time_format);
                },
                format: function (value) {
                    return moment(value, config.internal_date_time_format).format(config.input_date_time_format);
                },
                validate: function (value) {
                    return moment(value, config.input_date_time_format).isValid();
                }
            },
            checklist: __assign({}, baseHandler),
            select: __assign({}, baseHandler)
        };
        return handlers[type];
    };
    var basicOnChange = function (event, type, rules) {
        var _a = event.target, name = _a.name, value = _a.value;
        var inputHandler = getTypeHandler(type);
        var valid = event.target.checkValidity() && (isEmpty(value) || inputHandler.validate(value, rules));
        var parsedValue = isEmpty(value) ? value : (valid ? inputHandler.parse(value) : null);
        setState(function (state) {
            return __assign(__assign({}, state), { values: set(state.values, name, parsedValue), formattedValues: set(state.formattedValues, name, value), valid: set(state.valid, name, isValid(name)) });
        });
    };
    var checklistOnChange = function (event, value) {
        var name = event.target.name;
        setState(function (state) {
            var values = get(state.values, name) || [];
            var index = values.indexOf(value);
            if (index > -1) {
                values.splice(index, 1);
            }
            else {
                values.push(value);
            }
            return __assign(__assign({}, state), { values: set(state.values, name, values), valid: set(state.valid, name, isValid(name)) });
        });
    };
    var selectOnChange = function (event, options) {
        var _a = event.target, name = _a.name, value = _a.value;
        var index = parseInt(value, 10);
        setState(function (state) {
            return __assign(__assign({}, state), { values: set(state.values, name, options[index]), valid: set(state.valid, name, isValid(name)) });
        });
    };
    var onBlur = function (event, type) {
        var name = event.target.name;
        setState(function (state) {
            return __assign(__assign({}, state), { formattedValues: set(state.formattedValues, name, formatValue(name, type)) });
        });
    };
    var getValue = function (name) {
        return get(state.values, name);
    };
    var getInitialValue = function (name) {
        return get(state.initialValues, name);
    };
    var getRawValue = function (name) {
        return get(state.formattedValues, name);
    };
    var getFormattedValue = function (name, type) {
        var value = get(state.formattedValues, name);
        if (!isEmpty(value)) {
            return value;
        }
        return formatValue(name, type);
    };
    var formatValue = function (name, type) {
        var value = getValue(name);
        if (isEmpty(value)) {
            return '';
        }
        if (isValid(name)) {
            var inputHandler = getTypeHandler(type);
            return inputHandler.format(value);
        }
        return value + '';
    };
    var isValid = function (name) {
        return !has(state.valid, name) || get(state.valid, name);
    };
    var changed = function (name) {
        return !isEqual(getValue(name), getInitialValue(name));
    };
    var getErrors = function (name) {
        name = name.replace(/\[/g, '.').replace(/\]/g, '');
        return get(state.errors, name);
    };
    var isEmpty = function (value) {
        return typeof value == 'undefined' || value === '' || value === null;
    };
    var input = function (name) {
        return {
            name: name,
            valid: isValid(name),
            changed: changed(name),
            errors: getErrors(name)
        };
    };
    var string = function (name, rules) {
        return __assign({ type: 'text', value: getFormattedValue(name, 'string') || '', onChange: function (event) { return basicOnChange(event, 'string', rules); }, onBlur: function (event) { return onBlur(event, 'string'); }, maxLength: 255 }, input(name));
    };
    var password = function (name, rules) {
        return __assign({ type: 'password', value: getFormattedValue(name, 'string') || '', onChange: function (event) { return basicOnChange(event, 'string', rules); }, onBlur: function (event) { return onBlur(event, 'string'); } }, input(name));
    };
    var email = function (name, rules) {
        return __assign({ type: 'text', value: getFormattedValue(name, 'string') || '', onChange: function (event) { return basicOnChange(event, 'string', rules); }, onBlur: function (event) { return onBlur(event, 'string'); }, maxLength: 255 }, input(name));
    };
    var integer = function (name, rules) {
        return __assign({ type: 'text', value: getFormattedValue(name, 'integer') || '', onChange: function (event) { return basicOnChange(event, 'integer', rules); }, onBlur: function (event) { return onBlur(event, 'integer'); } }, input(name));
    };
    var money = function (name, rules) {
        return __assign({ type: 'text', value: getFormattedValue(name, 'money') || '', onChange: function (event) { return basicOnChange(event, 'money', rules); }, onBlur: function (event) { return onBlur(event, 'money'); } }, input(name));
    };
    var date = function (name, rules) {
        return __assign({ type: 'text', value: getFormattedValue(name, 'date') || '', onChange: function (event) { return basicOnChange(event, 'date', rules); }, onBlur: function (event) { return onBlur(event, 'date'); }, placeholder: config.input_date_format.toLowerCase() }, input(name));
    };
    var dateTime = function (name, rules) {
        return __assign({ type: 'text', value: getFormattedValue(name, 'dateTime') || '', onChange: function (event) { return basicOnChange(event, 'dateTime', rules); }, onBlur: function (event) { return onBlur(event, 'dateTime'); }, placeholder: config.input_date_time_format.toLowerCase() }, input(name));
    };
    var checklist = function (name, value) {
        return __assign({ type: 'checkbox', value: value, checked: (getValue(name) || []).indexOf(value) > -1, onChange: function (event) { return checklistOnChange(event, value); } }, input(name));
    };
    var select = function (name, options, key) {
        var optionsAreEqual = function (a, b) {
            if (key) {
                return get(a, key) == get(b, key);
            }
            return a == b;
        };
        var value = '';
        options.map(function (option, index) {
            if (optionsAreEqual(option, getValue(name))) {
                value = index;
            }
        });
        return __assign({ value: value, onChange: function (event) { return selectOnChange(event, options); } }, input(name));
    };
    var inputs = {
        string: string,
        password: password,
        email: email,
        integer: integer,
        money: money,
        date: date,
        dateTime: dateTime,
        checklist: checklist,
        select: select
    };
    var submit = function (event) {
        event.preventDefault();
        setState(function (state) {
            return __assign(__assign({}, state), { changed: null });
        });
        post(state.values);
    };
    var onError = function (errors) {
        setState(function (state) {
            return __assign(__assign({}, state), { errors: errors });
        });
    };
    var _b = useFetch(options.onSubmit, {
        onSuccess: options.onSuccess,
        onError: onError
    }), post = _b.post, loading = _b.loading;
    var hasChanges = function () {
        return !isEqual(state.values, state.initialValues);
    };
    var reset = function (values) {
        setState(function (state) {
            var initialValues = __assign(__assign(__assign({}, defaultState.initialValues), state.initialValues), values);
            return __assign(__assign({}, defaultState), { values: cloneDeep(initialValues), initialValues: cloneDeep(initialValues), formattedValues: {} });
        });
    };
    var initValues = function (values) {
        setState(function (state) {
            return __assign(__assign({}, state), { values: cloneDeep(values), initialValues: cloneDeep(values), formattedValues: getFormattedValues(values) });
        });
    };
    var getFormattedValues = function (values, path) {
        if (isPlainObject(values)) {
            return mapValues(values, function (value, key) {
                return getFormattedValues(value, (path ? path + '.' : '') + key);
            });
        }
        if (isArray(values)) {
            return map(values, function (value, index) {
                return getFormattedValues(value, "".concat(path || '', "[").concat(index, "]"));
            });
        }
        return null;
    };
    var setValue = function (name, value) {
        setState(function (state) {
            return __assign(__assign({}, state), { values: set(state.values, name, value), formattedValues: set(state.formattedValues, name, null) });
        });
    };
    var appendValue = function (name, value) {
        setState(function (state) {
            var values = get(state.values, name, []);
            var formattedValues = get(state.formattedValues, name, []);
            return __assign(__assign({}, state), { values: set(state.values, name, __spreadArray(__spreadArray([], values, true), [value], false)), formattedValues: set(state.formattedValues, name, __spreadArray(__spreadArray([], formattedValues, true), [setNested(cloneDeep(value), null)], false)) });
        });
    };
    var spliceValues = function (name, index, count) {
        var splice = function (list, index, count) {
            if (index >= list.length) {
                return;
            }
            list.splice(index, count);
        };
        setState(function (state) {
            var values = get(state.values, name, []);
            var formattedValues = get(state.formattedValues, name, []);
            var errors = state.errors || {};
            splice(values, index, count);
            splice(formattedValues, index, count);
            deleteByPrefix(errors, "".concat(name, ".").concat(index, "."));
            for (var i = index + 1; i < values.length + count; i++) {
                swapByPrefixes(errors, "".concat(name, ".").concat(i), "".concat(name, ".").concat(i - 1));
            }
            return __assign(__assign({}, state), { values: set(state.values, name, values), formattedValues: set(state.formattedValues, name, formattedValues), errors: errors });
        });
    };
    var moveUpValue = function (name, index) {
        var moveUp = function (list, index) {
            if (index == 0 || index >= list.length) {
                return;
            }
            var value = list[index];
            list[index] = list[index - 1];
            list[index - 1] = value;
        };
        var moveUpErrors = function (errors, name, index) {
            if (index == 0) {
                return;
            }
            var shouldMoveUp = "".concat(name, ".").concat(index);
            var shouldMoveDown = "".concat(name, ".").concat(index - 1);
            swapByPrefixes(errors, shouldMoveUp, shouldMoveDown);
        };
        setState(function (state) {
            var values = get(state.values, name, []);
            var formattedValues = get(state.formattedValues, name, []);
            var errors = state.errors || {};
            moveUp(values, index);
            moveUp(formattedValues, index);
            moveUpErrors(errors, name, index);
            return __assign(__assign({}, state), { values: set(state.values, name, values), formattedValues: set(state.formattedValues, name, formattedValues), errors: errors });
        });
    };
    var moveDownValue = function (name, index) {
        var moveDown = function (list, index) {
            if (index >= list.length) {
                return;
            }
            var value = list[index];
            list[index] = list[index + 1];
            list[index + 1] = value;
        };
        var moveDownErrors = function (errors, name, index) {
            var shouldMoveUp = "".concat(name, ".").concat(index + 1);
            var shouldMoveDown = "".concat(name, ".").concat(index);
            swapByPrefixes(errors, shouldMoveUp, shouldMoveDown);
        };
        setState(function (state) {
            var values = get(state.values, name, []);
            var formattedValues = get(state.formattedValues, name, []);
            var errors = state.errors || {};
            moveDown(values, index);
            moveDown(formattedValues, index);
            moveDownErrors(errors, name, index);
            return __assign(__assign({}, state), { values: set(state.values, name, values), formattedValues: set(state.formattedValues, name, formattedValues), errors: errors });
        });
    };
    return __assign(__assign(__assign({}, state), inputs), { submit: submit, loading: loading, getErrors: getErrors, hasChanges: hasChanges, reset: reset, initValues: initValues, setValue: setValue, appendValue: appendValue, spliceValues: spliceValues, moveUpValue: moveUpValue, moveDownValue: moveDownValue });
};
var Form = function (_a) {
    var form = _a.form, children = _a.children, props = __rest(_a, ["form", "children"]);
    return React.createElement("form", __assign({ noValidate: true, onSubmit: form.submit }, props), children);
};
var Input = function (_a) {
    var type = _a.type, valid = _a.valid, changed = _a.changed, errors = _a.errors, props = __rest(_a, ["type", "valid", "changed", "errors"]);
    var className = props.className || '';
    className += ' ' + gs({
        'form-control': ['text', 'number', 'email', 'password'].indexOf(type) > -1,
        'form-check-input': ['checkbox'].indexOf(type) > -1,
        'form-radio-input': ['radio'].indexOf(type) > -1,
        'is-invalid': changed && !valid || !changed && errors
    });
    var mergedProps = __assign(__assign({ type: type }, props), { className: className });
    return React.createElement("input", __assign({}, mergedProps));
};
var Select = function (_a) {
    var type = _a.type, valid = _a.valid, changed = _a.changed, errors = _a.errors, props = __rest(_a, ["type", "valid", "changed", "errors"]);
    var className = props.className || '';
    className += ' ' + gs('form-control', {
        'is-invalid': changed && !valid || !changed && errors
    });
    var mergedProps = __assign(__assign({ type: type }, props), { className: className });
    return React.createElement("select", __assign({}, mergedProps), props.children);
};
var Errors = function (props) {
    var t = useTranslation()[0];
    var translate = function (rule, parameters) {
        if (parameters === void 0) { parameters = {}; }
        return t("validation:".concat(rule), __assign(__assign({ attribute: t(props.attribute) }, props.errors[rule]), parameters));
    };
    var messages = {
        min_list: function (parameters) { return translate('min_list', { min: formatNumber(parameters.min) }); },
        min_string: function (parameters) { return translate('min_string', { min: formatNumber(parameters.min) }); },
        max_list: function (parameters) { return translate('max_list', { max: formatNumber(parameters.max) }); },
        max_string: function (parameters) { return translate('max_string', { max: formatNumber(parameters.max) }); },
        lt_money: function (parameters) { return translate('lt', { max: formatMoney(parameters.max) }); },
        lte_money: function (parameters) { return translate('lte', { max: formatMoney(parameters.max) }); },
        gt_money: function (parameters) { return translate('gt', { min: formatMoney(parameters.min) }); },
        gte_money: function (parameters) { return translate('gte', { min: formatMoney(parameters.min) }); },
        lt_numeric: function (parameters) { return translate('lt', { max: formatNumber(parameters.max) }); },
        lte_numeric: function (parameters) { return translate('lte', { max: formatNumber(parameters.max) }); },
        gt_numeric: function (parameters) { return translate('gt', { min: formatNumber(parameters.min) }); },
        gte_numeric: function (parameters) { return translate('gte', { min: formatNumber(parameters.min) }); }
    };
    return props.errors ? React.createElement("div", { className: gs('form-text', 'text-danger', 'small') }, Object.keys(props.errors).map(function (rule) { return React.createElement("div", { key: rule }, messages[rule] && messages[rule](props.errors[rule]) || translate(rule)); })) : null;
};
var SubmitButton = function (_a) {
    var form = _a.form, className = _a.className, children = _a.children;
    var t = useTranslation()[0];
    return React.createElement("button", { type: "submit", className: gs('btn', 'btn-primary') + ' ' + className, disabled: form.loading }, children || t('ok'));
};
var ResetButton = function (_a) {
    var form = _a.form, className = _a.className, children = _a.children;
    var t = useTranslation()[0];
    return React.createElement("button", { type: "button", onClick: function () { return form.reset(); }, className: gs('btn', 'btn-light') + ' ' + className }, children || t('reset'));
};
export { Form, Input, Select, Errors, SubmitButton, ResetButton, useForm };
